import { useEffect, useMemo, useState } from 'react';
import { Column, Row, useFilters, useGlobalFilter, usePagination, useSortBy, useTable } from 'react-table';
import { Box, Text, useDisclosure } from '@chakra-ui/react';

import DefaultColumnFilter from 'plugins/DITable/filters/DefaultColumnFilter';
import { linkStyle } from 'shared/styles';
import { useLazyGetAllClientProductsQuery } from 'core/API/productsApi';
import { IProductTitle, TProductSplit, TRetailerProp } from 'shared/types';
import { useGetCompanyInfoSelector, useGetUserInfoSelector } from 'core/redux/selectors/authSelectors';
import { useTranslation } from 'helpers/languages/translations';

import { parseProductsAliases } from './helper';
import { emptyProductTitle } from './consts';
import { IProductTitleRow } from 'pages/ProductTitles/types';
import { isUpcEnabled } from 'shared';

const useProductTitleContainer = () => {
    const translate = useTranslation();
    const companyInfo = useGetCompanyInfoSelector();

    const { isOpen, onOpen, onClose } = useDisclosure();
    const { isOpen: isTagOpen, onOpen: onTagOpen, onClose: OnTagClose } = useDisclosure();
    const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();

    const [getClientProducts, { data: clientProducts, isError, isLoading, isFetching }] =
        useLazyGetAllClientProductsQuery();

    const [productToUpdate, setProductToUpdate] = useState<IProductTitle>(emptyProductTitle);
    const [modalType, setModalType] = useState<'add' | 'update'>('add');
    const [retailer, setRetailer] = useState<TRetailerProp>('default');
    const [lang, setLang] = useState<string>('default');

    const isUpc = isUpcEnabled(companyInfo);

    const userInfo = useGetUserInfoSelector();
    const readonly = !userInfo?.user_groups?.includes(20);

    useEffect(() => {
        const getData = async () => {
            await getClientProducts({
                retailer: retailer === 'default' ? 'default' : `${retailer.id}`,
                lang,
                isBrut: false,
                isAliases: true,
                isProductTitle: true,
            });
        };

        getData().catch(console.error);
    }, [retailer, lang]);

    const data: Readonly<IProductTitle[]> = useMemo(() => {
        if (!isLoading && !isError && clientProducts) {
            return parseProductsAliases(clientProducts.products, isUpc);
        }

        return [];
    }, [clientProducts, isError, isLoading]);

    const columns: Readonly<Column<any>[]> = useMemo(() => {
        const handleClickProduct = (product: IProductTitle) => {
            setModalType('update');
            setProductToUpdate(product);
            onOpen();
        };

        const createdColumns: (Column<any> | null)[] = [
            {
                Header: isUpc ? 'UPC' : 'EAN',
                accessor: isUpc ? 'upc' : 'ean',
                Cell: ({ cell: { row, value } }) => {
                    const splitRow = row.original as IProductTitle;
                    return (
                        <Box onClick={() => handleClickProduct(splitRow)} {...linkStyle}>
                            {value}
                        </Box>
                    );
                },
            },
            {
                Header: translate('product.name') as string,
                accessor: 'name',
                minWidth: 300,
                width: '20%',
            },
        ];

        if (!isLoading && !isError && clientProducts) {
            clientProducts.splitsTitles.forEach((split, idx) => {
                createdColumns.push({
                    Header: /name_split/.test(split)
                        ? (translate('product.name_split_i', { idx: split.split('_').pop() }) as string)
                        : split,
                    accessor: `name_split_${idx + 1}`,
                    Cell: ({ cell: { value } }) => <Text>{(value as TProductSplit)?.val}</Text>,
                    // add a custom filter for nested columns
                    filter: (rows: Row<IProductTitleRow>[], columnIds: string[], filterValue: string) => {
                        return rows.filter((row) => {
                            const objectValue: string = row.original[split]?.val;
                            return objectValue?.toLowerCase().includes(filterValue?.toLowerCase());
                        });
                    },
                });
            });
        }

        const filteredColumns = createdColumns.filter((e): e is Column<any> => e !== null);
        return filteredColumns;
    }, [isLoading, isError, clientProducts]);

    const defaultColumn = useMemo(
        () => ({
            width: 150,
            Filter: DefaultColumnFilter,
        }),
        [],
    );

    const customGlobalFilter = (rows: Row<object>[], columnsIds: string[], filterValue: string) => {
        return rows.filter((row) => {
            const rowValues: (string | TProductSplit)[] = Object.values(row.original);
            return rowValues.some((rowValue) => {
                if (typeof rowValue === 'string') {
                    return rowValue.toLowerCase().includes(filterValue.toLowerCase());
                }
                if (typeof rowValue === 'object' && typeof rowValue?.val === 'string') {
                    return rowValue.val.toLowerCase().includes(filterValue.toLowerCase());
                }
                return false;
            });
        });
    };

    const tableInstance = useTable(
        {
            data,
            columns,
            // using a custom global filter because not all columns have the same structure
            globalFilter: customGlobalFilter,
            defaultColumn,
        },
        useFilters,
        useGlobalFilter,
        useSortBy,
        usePagination,
    );

    const props = useMemo(
        () => ({
            tableInstance,
            isLoading,
            isFetching,
            modalType,
            productToUpdate,
            isOpen,
            retailer,
            lang,
            isTagOpen,
            isDeleteOpen,
            clientProducts,
            isUpc,
            readonly,
            onOpen,
            onClose,
            onTagOpen,
            OnTagClose,
            onDeleteOpen,
            onDeleteClose,
            setProductToUpdate,
            setRetailer,
            setLang,
            setModalType,
        }),
        [
            tableInstance,
            isLoading,
            isFetching,
            modalType,
            productToUpdate,
            isOpen,
            retailer,
            lang,
            isTagOpen,
            isDeleteOpen,
            clientProducts,
            isUpc,
            readonly,
        ],
    );

    return props;
};

export type TProductTitleProps = ReturnType<typeof useProductTitleContainer>;

export default useProductTitleContainer;
